import { createContainer, createVar, type StyleRule, style } from '@vanilla-extract/css';

import { TIP_JAR_THEME_VARIABLES } from './TipJar.constants';

const themeVariable = (name: keyof typeof TIP_JAR_THEME_VARIABLES) =>
  `var(${name}, ${TIP_JAR_THEME_VARIABLES[name].fallback})`;

export const tokens = {
  surface: createVar(),
  ink: createVar(),
  muted: createVar(),
  faint: createVar(),
  line: createVar(),
  track: createVar(),
  tint: createVar(),
  primary: createVar(),
  onPrimary: createVar(),
  focusRing: createVar(),
  danger: createVar(),
  headerFont: createVar(),
  bodyFont: createVar(),
  buttonFont: createVar(),
  controlRadius: createVar(),
  fieldRadius: createVar(),
  cardRadius: createVar(),
};

// The page lays out by its own width rather than the viewport's, so a preview in a narrow frame lays out like a phone
// even on a wide screen.
export const pageContainer = createContainer();
export const WIDE_PAGE = `${pageContainer} (min-width: 640px)`;
export const DESKTOP_PAGE = `${pageContainer} (min-width: 900px)`;

// Community's pill shadow (`shadow-community-button`).
export const BUTTON_SHADOW = '0 2px 3px rgb(0 0 0 / 0.04), 0 4px 4px rgb(0 0 0 / 0.1)';
const CARD_SHADOW =
  '0 1px 2px rgb(0 0 0 / 0.04), 0 4px 12px -2px rgb(0 0 0 / 0.06), 0 24px 48px -16px rgb(0 0 0 / 0.14)';

export const FOCUS_OUTLINE = { outline: `2px solid ${tokens.primary}`, outlineOffset: '2px' };

export const SUPPORTS_COLOR_MIX = '(color: color-mix(in oklab, red 50%, blue))';

const mix = (color: string, percent: number, base: string) => `color-mix(in oklab, ${color} ${percent}%, ${base})`;

// Tints are the text mixed into its surface, so they hold on a dark theme as well as a light one.
const surfaceTokens = (surface: string, ink: string): StyleRule => {
  const primary = themeVariable('--wt-primary-color');
  const radius = themeVariable('--wt-border-radius');

  return {
    vars: {
      [tokens.surface]: surface,
      [tokens.ink]: ink,
      // Without color-mix: full-strength text, and a mid grey that shows on a light or dark surface.
      [tokens.muted]: ink,
      [tokens.faint]: ink,
      [tokens.line]: 'rgb(128 128 128 / 0.3)',
      [tokens.track]: 'rgb(128 128 128 / 0.2)',
      [tokens.tint]: 'rgb(128 128 128 / 0.1)',
      [tokens.focusRing]: 'rgb(128 128 128 / 0.35)',
      [tokens.danger]: '#B42318',
      [tokens.primary]: primary,
      [tokens.onPrimary]: themeVariable('--wt-text-on-primary-color'),
      [tokens.headerFont]: themeVariable('--wt-header-font'),
      [tokens.bodyFont]: themeVariable('--wt-body-font'),
      [tokens.buttonFont]: themeVariable('--wt-button-font'),
      // Controls follow the site's buttons. Fields and cards round more, in step, up to 12px and 24px.
      [tokens.controlRadius]: radius,
      [tokens.fieldRadius]: `min(calc(${radius} * 2), 12px)`,
      [tokens.cardRadius]: `min(calc(${radius} * 3), 24px)`,
    },
    '@supports': {
      [SUPPORTS_COLOR_MIX]: {
        vars: {
          [tokens.muted]: mix(ink, 72, surface),
          [tokens.faint]: mix(ink, 45, surface),
          [tokens.line]: mix(ink, 10, surface),
          [tokens.track]: mix(ink, 7, surface),
          [tokens.tint]: mix(ink, 4, surface),
          [tokens.focusRing]: mix(primary, 22, 'transparent'),
          // Darker on a light surface, lighter on a dark one.
          [tokens.danger]: mix('#D92D20', 80, ink),
        },
      },
    },
  };
};

export const pageSurface = style(
  surfaceTokens(themeVariable('--wt-background-color'), themeVariable('--wt-text-on-background-color')),
);

const cardSurface = style(
  surfaceTokens(
    themeVariable('--wt-subscribe-background-color'),
    themeVariable('--wt-text-on-subscribe-background-color'),
  ),
);

export const card = style([
  cardSurface,
  {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    padding: '24px 20px',
    border: `1px solid ${tokens.line}`,
    borderRadius: tokens.cardRadius,
    background: tokens.surface,
    color: tokens.ink,
    boxShadow: CARD_SHADOW,
    '@container': {
      [WIDE_PAGE]: { padding: '40px' },
    },
  },
]);

// A 44px tap target around a line of text, without the extra height pushing the layout around.
export const textButton = style({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  minHeight: '44px',
  margin: '-12px -6px',
  padding: '0 6px',
  border: 0,
  borderRadius: '8px',
  background: 'none',
  color: tokens.ink,
  font: 'inherit',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
  cursor: 'pointer',
  textDecoration: 'underline',
  textDecorationColor: tokens.faint,
  textUnderlineOffset: '3px',
  selectors: {
    '&:hover': { textDecorationColor: 'currentColor' },
    '&:focus-visible': FOCUS_OUTLINE,
  },
});
